import { FUSO_EXIBICAO, formatCpf, orDash } from 'src/app/shared/tabela';

/**
 * Viewmodel de uma linha de Check-ins.
 *
 * Existe para tirar do template as cadeias de condição que hoje se repetem em
 * três lugares — a linha da tabela, o card mobile e os diálogos de
 * sucesso/erro — e que já divergiram entre si. Tudo aqui é função pura de
 * `(linha crua, contexto)`: nada de `Date.now()` escondido, nada de HTML em
 * string, nada de emoji como ícone. Quem renderiza escolhe o ícone a partir do
 * status semântico.
 *
 * Substitui `checkinWindowStatus`, `janelaStatus`, `statusIcon`,
 * `renderCheckinWindow(s)` e `renderJanelaItem` do componente.
 */

export type JanelaStatus = 'ativa' | 'futura' | 'encerrada';

export type BadgeStatus = 'success' | 'warning' | 'danger' | 'brand' | 'neutral';

export interface JanelaVm {
  /**
   * Id da janela no backend (`JanelaCheckinDto.id`), quando veio. É o que o
   * path do check-in retroativo exige — sem ele a ação não é oferecida.
   */
  id: string | null;
  label: string | null;
  /**
   * `25/08 das 07:00 às 23:00` quando começa e termina no mesmo dia (no fuso do
   * evento), `25/08 22:00 → 26/08 12:00` quando atravessa a meia-noite. O
   * formato longo repetia a data duas vezes e ocupava a linha inteira.
   */
  periodoCurto: string;
  /**
   * O check-in ATRIBUÍDO a esta janela, quando o instante cai dentro dela.
   *
   * É inferência do cliente, não dado do backend: `JanelaCheckinDto` traz só
   * id/inicio/fim/label — a API não diz em qual janela o check-in aconteceu.
   * Check-in forçado, fora de qualquer janela, não é atribuído a nenhuma (e
   * segue visível no cabeçalho do dropdown).
   */
  checkin: { horario: string; operador: string | null } | null;
  /**
   * O que a célula MOSTRA quando não houve check-in, que não é a mesma coisa
   * em toda janela: uma janela ENCERRADA sem check-in é um fato fechado e
   * ganha `--`; uma janela futura ainda não tem o que dizer e fica em branco.
   * Em branco nas duas, a pessoa não distingue "não entrou" de "ainda pode".
   */
  celulaCheckin: string;
  celulaOperador: string;
  status: JanelaStatus;
  statusLabel: string;
  badgeStatus: BadgeStatus;
  /** `dd/MM/yyyy HH:mm – dd/MM/yyyy HH:mm`, no fuso do evento. */
  periodo: string;
  /**
   * Os limites CRUS da janela, em instante UTC ISO — `periodo`/`periodoCurto`
   * são texto e não voltam a virar data. É deles que sai o `min`/`max` do
   * `datetime-local` do check-in retroativo.
   */
  inicio: string | null;
  fim: string | null;
  /**
   * A janela aceita check-in retroativo AGORA: o backend ligou a flag
   * (`checkinRetroativoHabilitado`) e ninguém entrou por esta janela ainda.
   *
   * Exige também id, início e fim — o endpoint recebe a janela no path e
   * valida o horário dentro de `[inicio, fim]`, e o backend só deixa ligar a
   * flag em janela com os dois preenchidos (ADR-0021).
   */
  retroativoDisponivel: boolean;
}

export type CheckinStatus = 'presente' | 'pendente' | 'expirado';

export type CheckinActionKey =
  | 'checkin'
  | 'desfazer'
  | 'reenviar'
  | 'certificado'
  | 'imprimirEtiqueta'
  | 'credenciamento'
  | 'camposExtras';

export interface CheckinVmContext {
  /** Instante de referência — injetado para o teste não depender do relógio. */
  agora: Date;
  /** Config do evento libera certificado de participação. */
  podeCertificado: boolean;
  /**
   * Quem pode mandar uma etiqueta para a impressora térmica.
   *
   * Só CASL, sem exigir impressora conectada: sem ela o modal abre no
   * impedimento `sem-impressora`, que diz o motivo e oferece Conectar num
   * clique. Esconder a ação devolveria o clique silencioso que o impedimento
   * existe para acabar.
   */
  podeImprimirEtiqueta: boolean;
  /**
   * Papel de gestão pode reverter um check-in.
   *
   * ponytail: hoje a tela passa `false` — não existe endpoint de reversão
   * (`INGRESSOS_CHECKIN_POR_CODIGO` só faz PUT). Quando o backend expuser o
   * DELETE, é só passar a permissão real; a ação já está modelada aqui, nos
   * testes e no design.
   */
  podeDesfazer: boolean;
  /** Preferências de visibilidade do usuário (localStorage). */
  visiveis: Record<CheckinActionKey, boolean>;
}

export interface CheckinRowVm {
  /** Chave estável de linha (`track` do @for, seleção da tabela). */
  id: string;
  /**
   * O id do INGRESSO, que é o que os paths da v3 pedem
   * (`/eventos/{eventoId}/ingressos/{ingressoId}/...`). `id` acima aceita
   * fallback para o código do ingresso, então não serve de path param.
   */
  ingressoId: string;
  /**
   * Fuso do evento (IANA), para converter hora-de-parede em instante — o que o
   * check-in retroativo manda no corpo (ADR-0009).
   */
  timezone: string;
  /**
   * A linha crua, como veio do backend. Existe só para as peças que ainda
   * falam o dialeto antigo — hoje o modal de impressão, que recebe o ingresso
   * inteiro. NÃO leia daqui no template: o que a tela mostra sai dos campos
   * já formatados acima.
   */
  bruto: Record<string, unknown>;
  nome: string;
  email: string;
  codigo: string;
  /** CPF/CNPJ mascarado, ou `--`. */
  documento: string;
  lote: string;
  operador: string;
  /** `dd/MM/yyyy HH:mm` no fuso do evento, ou `--`. */
  dataCheckinLabel: string;
  /**
   * As janelas resumidas numa pílula só, para a célula da tabela: aberta ganha
   * do resto, encerrada só quando não há nenhuma por vir. A lista completa fica
   * em `janelas` (o card mobile mostra período por período).
   */
  janelaResumo: string;
  janelaBadge: BadgeStatus | null;
  /**
   * A janela que importa AGORA: a aberta, senão a próxima a abrir, senão a
   * última encerrada. Lote com janela diária chega com 15+ — o card mostra
   * esta e conta o resto em `janelasRestantes`, em vez de virar uma parede de
   * pílulas (bug pego rodando a tela, não no teste).
   */
  janelaDestaque: JanelaVm | null;
  janelasRestantes: number;
  /** `camposExtras` (mapa livre da importação) achatado em label/valor. */
  camposExtras: { label: string; value: string }[];
  temCamposExtras: boolean;
  observacoes: string;
  presente: boolean;
  status: CheckinStatus;
  statusLabel: string;
  badgeStatus: BadgeStatus;
  janelas: JanelaVm[];
  /** Alguma janela aberta no instante de referência. */
  janelaAberta: boolean;
  /**
   * Um check-in agora cairia no 409 `IngressoExpiradoException` e abriria o
   * modal "Ingresso Expirado" com o botão Forçar Check-in. Sabendo disso
   * antes, a tela avisa sem gastar a ida ao servidor.
   */
  forcaraCheckin: boolean;
  /** Fonte única do card, do bottom sheet e do modal de sucesso. */
  detalhes: { label: string; value: string }[];
  acoes: CheckinActionKey[];
  /** Ação de rodapé do card; `null` quando não sobra nenhuma. */
  acaoPrimaria: CheckinActionKey | null;
  /** Resto — vai para o kebab (desktop) ou para o action sheet (mobile). */
  acoesSecundarias: CheckinActionKey[];
}

const SP_DIA_MES = new Intl.DateTimeFormat('pt-BR', {
  timeZone: FUSO_EXIBICAO,
  day: '2-digit',
  month: '2-digit',
});

const SP_HORA = new Intl.DateTimeFormat('pt-BR', {
  timeZone: FUSO_EXIBICAO,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const SP_DATA_HORA = new Intl.DateTimeFormat('pt-BR', {
  timeZone: FUSO_EXIBICAO,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** `dd/MM/yyyy HH:mm` no fuso do evento, ou `null` para valor inválido/vazio. */
function dataHora(v: unknown): string | null {
  if (v === null || v === undefined || v === '') return null;
  const d = v instanceof Date ? v : new Date(String(v));
  if (Number.isNaN(d.getTime())) return null;
  return SP_DATA_HORA.format(d).replace(', ', ' ');
}

function instante(v: unknown): Date | null {
  if (v === null || v === undefined || v === '') return null;
  const d = v instanceof Date ? v : new Date(String(v));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** `25/08 das 07:00 às 23:00` ou `25/08 22:00 → 26/08 12:00`. */
function periodoCurto(inicio: unknown, fim: unknown): string {
  const i = instante(inicio);
  const f = instante(fim);
  if (!i && !f) return '--';
  if (!i) return `até ${SP_DIA_MES.format(f!)} ${SP_HORA.format(f!)}`;
  if (!f) return `a partir de ${SP_DIA_MES.format(i)} ${SP_HORA.format(i)}`;
  const mesmoDia = SP_DIA_MES.format(i) === SP_DIA_MES.format(f);
  return mesmoDia
    ? `${SP_DIA_MES.format(i)} das ${SP_HORA.format(i)} às ${SP_HORA.format(f)}`
    : `${SP_DIA_MES.format(i)} ${SP_HORA.format(i)} → ${SP_DIA_MES.format(f)} ${SP_HORA.format(f)}`;
}

const JANELA_LABEL: Record<JanelaStatus, string> = {
  ativa: 'Aberta',
  futura: 'Ainda não abriu',
  encerrada: 'Encerrada',
};

const JANELA_BADGE: Record<JanelaStatus, BadgeStatus> = {
  ativa: 'success',
  futura: 'brand',
  encerrada: 'neutral',
};

/** Mesma regra do antigo `checkinWindowStatus`, com o agora injetado. */
function statusDaJanela(inicio: unknown, fim: unknown, agora: Date): JanelaStatus | null {
  if (!inicio && !fim) return null;
  if (inicio && agora < new Date(String(inicio))) return 'futura';
  if (fim && agora > new Date(String(fim))) return 'encerrada';
  return 'ativa';
}

function janelaVm(
  label: unknown,
  inicio: unknown,
  fim: unknown,
  agora: Date,
  checkinEm: Date | null,
  operador: string | null,
  /** Registro PRÓPRIO da janela, quando o backend manda (ver JanelaVm.checkin). */
  registroDaJanela?: {
    id?: unknown;
    dataCheckin?: unknown;
    operadorCheckin?: unknown;
    checkinRetroativoHabilitado?: unknown;
  },
): JanelaVm | null {
  const status = statusDaJanela(inicio, fim, agora);
  if (!status) return null;
  const periodo = [dataHora(inicio), dataHora(fim)].filter(Boolean).join(' – ');

  const i = instante(inicio);
  const f = instante(fim);

  // Dado real vence inferência: `JanelaCheckinDto` traz dataCheckin e
  // operadorCheckin da PRÓPRIA janela na listagem do operador. Só quando ele
  // não vem é que caímos em deduzir pelo intervalo — e a dedução só consegue
  // atribuir um check-in, enquanto o campo real cobre entrada e saída em
  // janelas diferentes.
  const proprio = instante(registroDaJanela?.dataCheckin);
  const dentro = !!checkinEm && (!i || checkinEm >= i) && (!f || checkinEm <= f);
  const checkin = proprio
    ? {
        horario: SP_HORA.format(proprio),
        operador: registroDaJanela?.operadorCheckin
          ? String(registroDaJanela.operadorCheckin)
          : operador,
      }
    : dentro
      ? { horario: SP_HORA.format(checkinEm as Date), operador }
      : null;

  // Só a janela encerrada troca o vazio por traço — ver JanelaVm.celulaCheckin.
  const vazio = status === 'encerrada' ? '--' : '';

  const id = registroDaJanela?.id ? String(registroDaJanela.id) : null;
  const inicioIso = i ? i.toISOString() : null;
  const fimIso = f ? f.toISOString() : null;

  return {
    id,
    label: label ? String(label) : null,
    periodoCurto: periodoCurto(inicio, fim),
    checkin,
    celulaCheckin: checkin?.horario ?? vazio,
    celulaOperador: checkin?.operador ?? vazio,
    status,
    statusLabel: JANELA_LABEL[status],
    badgeStatus: JANELA_BADGE[status],
    periodo: periodo || '--',
    inicio: inicioIso,
    fim: fimIso,
    // `!checkin` e não `!registroDaJanela.dataCheckin`: se a tela já atribuiu
    // uma entrada a esta janela — por dado do backend OU por inferência —,
    // oferecer "registrar o horário que faltou" seria oferecer a duplicata que
    // o índice único do banco recusa com 409.
    retroativoDisponivel:
      registroDaJanela?.checkinRetroativoHabilitado === true &&
      !checkin &&
      !!id &&
      !!inicioIso &&
      !!fimIso,
  };
}

type LinhaCrua = Record<string, unknown>;

/** `checkin | checkIn | dataCheckin` — as três grafias que o backend usa. */
function jaEntrou(row: LinhaCrua): boolean {
  return !!(row['checkin'] || row['checkIn'] || row['dataCheckin']);
}

function temIdentificador(row: LinhaCrua): boolean {
  return !!(row['id'] || row['ingressoId'] || row['codigoIngresso']);
}

/** Ordem do rodapé do card: a primeira disponível vira a primária. */
const PRIORIDADE: CheckinActionKey[] = [
  'checkin',
  'certificado',
  'reenviar',
  'imprimirEtiqueta',
  'credenciamento',
  'camposExtras',
  'desfazer',
];

export function buildCheckinRowVm(row: LinhaCrua, ctx: CheckinVmContext): CheckinRowVm {
  const presente = jaEntrou(row);

  const checkinEm = instante(row['dataCheckin'] ?? row['checkin'] ?? row['checkIn']);
  const operadorCru = row['operadorCheckin'];
  const operadorNome = operadorCru ? String(operadorCru) : null;

  const cruas = Array.isArray(row['janelasCheckin']) ? (row['janelasCheckin'] as LinhaCrua[]) : [];
  const janelas: JanelaVm[] = cruas.length
    ? cruas
        .map((j) =>
          janelaVm(j?.['label'], j?.['inicio'], j?.['fim'], ctx.agora, checkinEm, operadorNome, {
            id: j?.['id'],
            dataCheckin: j?.['dataCheckin'],
            operadorCheckin: j?.['operadorCheckin'],
            checkinRetroativoHabilitado: j?.['checkinRetroativoHabilitado'],
          }),
        )
        .filter((j): j is JanelaVm => j !== null)
    : [
        janelaVm(
          null,
          row['inicioCheckin'],
          row['terminoCheckin'],
          ctx.agora,
          checkinEm,
          operadorNome,
        ),
      ].filter((j): j is JanelaVm => j !== null);

  const janelaAberta = janelas.some((j) => j.status === 'ativa');

  /**
   * Uma janela SEM check-in é uma entrada que ainda pode acontecer, mesmo que
   * a pessoa já tenha entrado por outra — lote com janela diária é o caso
   * comum, e `!presente` desabilitava o botão para sempre no primeiro
   * check-in. Só fica sem o que fazer quando TODAS as janelas já foram
   * cumpridas. Sem janela configurada, vale o antigo: um check-in e acabou.
   */
  const janelasPendentes = janelas.filter((j) => !j.checkin);
  const podeCheckin = janelas.length > 0 ? janelasPendentes.length > 0 : !presente;

  /**
   * Forçar é o check-in que cai FORA de janela aberta — e só faz sentido para
   * quem AINDA NÃO ENTROU. É a saída para erro e para quem chegou fora do
   * horário do lote; não é uma segunda entrada.
   *
   * O `!presente` fechou o caso que faltava: quem já entrou e ainda tem uma
   * janela FUTURA pela frente caía aqui (há janela pendente, nenhuma aberta
   * agora) e ganhava um botão "Forçar check-in" — enquanto o badge dizia
   * "Presente". Forçar a entrada de quem já está dentro não quer dizer nada.
   */
  const janelaAbertaPendente = janelas.some((j) => j.status === 'ativa' && !j.checkin);
  const forcaraCheckin = podeCheckin && !presente && janelas.length > 0 && !janelaAbertaPendente;

  const status: CheckinStatus = presente ? 'presente' : forcaraCheckin ? 'expirado' : 'pendente';
  // "Expirado" e não "Encerrado": `Encerrada` já é o status da JANELA, e usar a
  // mesma palavra para o ingresso borraria dois conceitos na mesma linha.
  // Casa também com o `IngressoExpiradoException` que o backend devolve.
  const statusLabel = { presente: 'Presente', pendente: 'Pendente', expirado: 'Expirado' }[status];
  const badgeStatus = ({ presente: 'success', pendente: 'neutral', expirado: 'warning' } as const)[status];

  // Aberta ganha do resto; encerrada só quando não sobrou nenhuma por vir.
  const resumo: JanelaStatus | null = janelas.length === 0
    ? null
    : janelaAberta
      ? 'ativa'
      : janelas.some((j) => j.status === 'futura')
        ? 'futura'
        : 'encerrada';

  const janelaDestaque =
    janelas.find((j) => j.status === 'ativa') ??
    janelas.find((j) => j.status === 'futura') ??
    janelas.at(-1) ??
    null;

  const extrasCrus = row['camposExtras'];
  const camposExtras =
    extrasCrus && typeof extrasCrus === 'object' && !Array.isArray(extrasCrus)
      ? Object.entries(extrasCrus as Record<string, unknown>).map(([label, valor]) => ({
          label,
          value: typeof valor === 'boolean' ? (valor ? 'Sim' : 'Não') : orDash(valor),
        }))
      : [];

  // Mesmos campos e mesma ordem do modal "Check-in Realizado!".
  const detalhes = [
    { label: 'Nome', value: orDash(row['nomeParticipante']) },
    { label: 'E-mail', value: orDash(row['emailParticipante']) },
    { label: 'CPF', value: row['cpf'] ? formatCpf(row['cpf']) : '--' },
    { label: 'Código do Ingresso', value: orDash(row['codigoIngresso']) },
    { label: 'Lote', value: orDash(row['nomeLote']) },
    ...(presente
      ? [{ label: 'Data do Check-in', value: dataHora(row['dataCheckin']) ?? '--' }]
      : []),
  ];

  const disponivel: Record<CheckinActionKey, boolean> = {
    // Quem já entrou só reaparece com o botão quando há uma janela ABERTA
    // agora — a segunda entrada do lote com janela diária, que é legítima.
    // Fora disso o botão sumiria para dar lugar a um "forçar" sem sentido.
    checkin: podeCheckin && (!presente || janelaAbertaPendente),
    desfazer: presente && ctx.podeDesfazer,
    reenviar: !presente && temIdentificador(row),
    certificado: presente && ctx.podeCertificado,
    imprimirEtiqueta: ctx.podeImprimirEtiqueta,
    credenciamento: true,
    camposExtras: true,
  };

  const acoes = PRIORIDADE.filter((k) => disponivel[k] && ctx.visiveis[k]);
  const acaoPrimaria = acoes[0] ?? null;

  return {
    id: String(row['id'] ?? row['ingressoId'] ?? row['codigoIngresso'] ?? ''),
    ingressoId: String(row['ingressoId'] ?? row['id'] ?? ''),
    // ponytail: fallback ate GUE-429 expor timezone nesta tela
    timezone: typeof row['timezone'] === 'string' && row['timezone'] ? row['timezone'] : FUSO_EXIBICAO,
    bruto: row,
    nome: orDash(row['nomeParticipante']),
    email: orDash(row['emailParticipante']),
    codigo: orDash(row['codigoIngresso']),
    documento: row['cpf'] ? formatCpf(row['cpf']) : '--',
    lote: orDash(row['nomeLote']),
    operador: orDash(row['operadorCheckin']),
    dataCheckinLabel: dataHora(row['dataCheckin']) ?? '--',
    janelaResumo: resumo ? JANELA_LABEL[resumo] : '--',
    janelaBadge: resumo ? JANELA_BADGE[resumo] : null,
    janelaDestaque,
    janelasRestantes: Math.max(0, janelas.length - 1),
    camposExtras,
    temCamposExtras: camposExtras.length > 0,
    observacoes: orDash(row['observacoes']),
    presente,
    status,
    statusLabel,
    badgeStatus,
    janelas,
    janelaAberta,
    forcaraCheckin,
    detalhes,
    acoes,
    acaoPrimaria,
    acoesSecundarias: acoes.slice(1),
  };
}
